import React, { useEffect, useState } from "react";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  X, Wallet, AlertTriangle, Trash2, Plus, Loader2,
} from "lucide-react";

const fmtEur = (v) =>
  new Intl.NumberFormat("ca-ES", { style: "currency", currency: "EUR" })
    .format(Number(v) || 0);

/**
 * Per-category monthly budget editor. Reads & writes /api/budgets.
 * Renders inline progress bars that turn amber at 80% and rose at 100%.
 */
export default function BudgetsDialog({ open, onClose, suggestedCategories = [] }) {
  const { lang } = useI18n();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [newLimit, setNewLimit] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/budgets");
      setList(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const upsert = async (category, monthly_limit) => {
    try {
      await api.post("/budgets", { category, monthly_limit, is_active: true });
      toast.success(lang === "es" ? "Guardado" : "Desat");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const addNew = async () => {
    const limit = parseFloat(newLimit);
    if (!limit || limit <= 0) {
      toast.error(lang === "es" ? "Límite inválido" : "Límit no vàlid");
      return;
    }
    await upsert(newCategory.trim(), limit);
    setNewCategory("");
    setNewLimit("");
  };

  const remove = async (id) => {
    if (!window.confirm(lang === "es" ? "¿Eliminar presupuesto?" : "Eliminar pressupost?")) return;
    try {
      await api.delete(`/budgets/${id}`);
      toast.success(lang === "es" ? "Eliminado" : "Eliminat");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  if (!open) return null;
  const existingCats = new Set(list.map((b) => b.category || ""));
  const suggestions = suggestedCategories.filter((c) => !existingCats.has(c));

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-150"
      onClick={onClose}
      data-testid="budgets-dialog"
    >
      <div className="w-full sm:max-w-xl max-h-[92vh] overflow-y-auto bg-[#0B1220] border-t sm:border border-white/10 sm:rounded-2xl shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-[#0B1220] border-b border-white/10 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wallet size={16} className="text-emerald-300" />
            <h3 className="font-display text-lg font-semibold text-white">
              {lang === "es" ? "Presupuestos por categoría" : "Pressupostos per categoria"}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5 text-slate-400">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-xs text-slate-400">
            {lang === "es"
              ? "Define un límite mensual por categoría. Recibirás un aviso en la campana al superar el 80% y el 100%."
              : "Defineix un límit mensual per categoria. Rebràs un avís a la campaneta en superar el 80% i el 100%."}
          </p>
          {/* Existing budgets */}
          <div className="space-y-2">
            {loading && list.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <Loader2 className="animate-spin inline" size={16} />
              </div>
            ) : list.length === 0 ? (
              <div className="text-xs text-slate-500 text-center py-4 border border-dashed border-white/10 rounded-lg">
                {lang === "es" ? "Sin presupuestos definidos" : "Sense pressupostos definits"}
              </div>
            ) : list.map((b) => {
              const pct = Number(b.pct) || 0;
              const isOver = pct >= 100;
              const isWarn = pct >= 80 && pct < 100;
              const barColor = isOver ? "bg-rose-500" : isWarn ? "bg-amber-500" : "bg-emerald-500";
              const label = b.category || (lang === "es" ? "Sin categoría" : "Sense categoria");
              return (
                <div key={b.id} className="p-3 rounded-lg border border-white/10 bg-white/[0.02]" data-testid={`budget-row-${b.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="flex-1 text-sm font-semibold text-white capitalize">{label}</span>
                    {(isOver || isWarn) && (
                      <AlertTriangle size={12} className={isOver ? "text-rose-400" : "text-amber-400"} />
                    )}
                    <Input
                      type="number"
                      step="0.01"
                      defaultValue={b.monthly_limit}
                      onBlur={(e) => {
                        const v = parseFloat(e.target.value);
                        if (v > 0 && v !== b.monthly_limit) upsert(b.category, v);
                      }}
                      className="bg-white/5 border-white/10 font-mono text-xs h-7 w-28 text-right"
                      data-testid={`budget-limit-${b.id}`}
                    />
                    <span className="text-xs text-slate-500">€/mes</span>
                    <button onClick={() => remove(b.id)}
                            className="p-1 rounded hover:bg-rose-500/20 text-slate-500 hover:text-rose-300"
                            data-testid={`budget-delete-${b.id}`}>
                      <Trash2 size={11} />
                    </button>
                  </div>
                  <div className="flex items-center gap-2 text-[11px]">
                    <span className="text-slate-400 font-mono">
                      {fmtEur(b.spent)} / {fmtEur(b.monthly_limit)}
                    </span>
                    <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${barColor}`}
                           style={{ width: `${Math.min(pct, 100)}%` }} />
                    </div>
                    <span className={`font-mono font-semibold w-12 text-right ${
                      isOver ? "text-rose-300" : isWarn ? "text-amber-300" : "text-slate-300"
                    }`}>{pct.toFixed(0)}%</span>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Add new budget */}
          <div className="border-t border-white/10 pt-4 space-y-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              {lang === "es" ? "Añadir presupuesto" : "Afegir pressupost"}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder={lang === "es" ? "Categoría (vacía = sin categoría)" : "Categoria (buida = sense categoria)"}
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                list="budget-cat-list"
                className="bg-white/5 border-white/10 text-sm flex-1"
                data-testid="budget-new-category"
              />
              <datalist id="budget-cat-list">
                {suggestions.map((c) => <option key={c} value={c} />)}
              </datalist>
              <Input
                type="number"
                step="0.01"
                placeholder="€/mes"
                value={newLimit}
                onChange={(e) => setNewLimit(e.target.value)}
                className="bg-white/5 border-white/10 font-mono text-sm w-28"
                data-testid="budget-new-limit"
              />
              <Button onClick={addNew} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="budget-add-btn">
                <Plus size={14} />
              </Button>
            </div>
            {suggestions.length > 0 && (
              <div className="text-[10px] text-slate-500">
                {lang === "es" ? "Sugerencias:" : "Suggeriments:"}{" "}
                {suggestions.slice(0, 6).map((c, i) => (
                  <button key={c} onClick={() => setNewCategory(c)}
                          className="text-blue-300 hover:underline mr-2 capitalize">
                    {c}{i < Math.min(5, suggestions.length - 1) ? "," : ""}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
